import { SkFont } from "@shopify/react-native-skia";
import React, { memo } from "react";

import PlatformChart from "@/components/molecules/PlatformChart";
import { PlatformChartData } from "@/components/molecules/PlatformChart/PlatformChartView";

/**
 * Data structure for chart points in ComparePlayersChart
 * Each data point can have multiple string or number properties
 */
export interface ComparePlayersChartData {
  [key: string]: string | number;
}

/**
 * Props interface for ComparePlayersChart component (Container input)
 */
export interface ComparePlayersChartProps {
  data: ComparePlayersChartData[];
  xKey: string;
  yKeys: string[];
  colors: Record<string, string>;
  font?: SkFont | null;
  width?: number;
  height?: number;
  domainPadding?: number;
  padding?: number;
  xAxis?: {
    labelColor?: string;
    formatXLabel?: (value: string | number) => string;
    tickCount?: number;
  };
  yAxis?: {
    labelColor?: string;
  }[];
}

/**
 * Props interface for ComparePlayersChartView (View component with calculated values)
 */
export interface ComparePlayersChartViewProps extends ComparePlayersChartProps {
  yDomainMin: number;
  yTickValues?: number[];
}

/**
 * Web-only chart component for compare players feature
 * Pure presentation component that receives calculated Y-axis values from Container
 * @param props - Chart props including calculated yDomainMin and yTickValues
 * @param props.data - Chart data points with metric values
 * @param props.xKey - Key for X-axis values (typically date or game)
 * @param props.yKeys - Array of keys for Y-axis values (metric names)
 * @param props.colors - Color mapping for each metric line
 * @param props.width - Chart width in pixels
 * @param props.height - Chart height in pixels
 * @param props.domainPadding - Padding around chart domain
 * @param props.padding - Chart container padding
 * @param props.xAxis - X-axis configuration (label colors, formatting, tick count)
 * @param props.yAxis - Y-axis configuration array for each metric
 * @param props.yDomainMin - Calculated minimum Y-axis value from Container
 * @param props.yTickValues - Calculated Y-axis tick values from Container
 * @param props.font - Skia font for chart labels (nullable)
 */
const ComparePlayersChartView: React.FC<ComparePlayersChartViewProps> = ({
  data,
  xKey,
  yKeys,
  colors,
  font,
  width = 600,
  height = 384,
  domainPadding = 20,
  padding = 50,
  xAxis,
  yAxis,
  yDomainMin,
  yTickValues,
}) => (
  <PlatformChart
    data={data as PlatformChartData[]}
    xKey={xKey}
    yKeys={yKeys}
    colors={colors}
    font={font}
    width={width}
    height={height}
    domainPadding={domainPadding}
    padding={padding}
    xAxis={{
      ...xAxis,
      axisValue: yDomainMin,
    }}
    yAxis={[
      {
        labelColor: yAxis?.[0]?.labelColor,
        tickValues: yTickValues,
        tickFormat: (t: number) => t.toFixed(1),
      },
    ]}
  />
);

ComparePlayersChartView.displayName = "ComparePlayersChartView";

export default memo(ComparePlayersChartView);
